import { call, put, select, takeLatest } from 'typed-redux-saga'
import {
	BankEmployeesModel,
	bankEmployeesApi,
	BankEmployeesNS,
	bankEmployeesSlice,
	bankEmployeesSelectors,
} from 'Pages/bankEmployees'
import { errorHandler, LocalStorage, loadersSlice, gridSlice } from 'Modules'
import { RequestParams, ResponseCatalog } from 'Models'
import { Deserialize } from 'cerialize'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'
import { socketSlice } from 'Modules/Socket'

function* getAll(action: BankEmployeesNS.GetAll) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const { params, clear, callback } = action.payload
		const { page, per_page, query, ...fields } = params
		const requestParams = new RequestParams(clear ? 1 : page, per_page, query, fields)
		if (clear) {
			yield* put(bankEmployeesSlice.actions.clearAll())
		}
		const res = yield* call(bankEmployeesApi.getAll, requestParams)
		const bankEmployees = new ResponseCatalog<BankEmployeesModel>(res)
		bankEmployees.entries = bankEmployees.entries.map(
			(item): BankEmployeesModel => Deserialize(item, BankEmployeesModel)
		)
		if (!clear) {
			const oldBankEmployees = yield* select(bankEmployeesSelectors.selectEntries)
			bankEmployees.entries = oldBankEmployees.concat(res.entries)
		}
		yield* put(bankEmployeesSlice.actions.setAll(bankEmployees))
		yield* put(bankEmployeesSlice.actions.setRequestParams(requestParams))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* createOne(action: BankEmployeesNS.CreateOne) {
	try {
		const { data, callback } = action.payload
		yield* put(bankEmployeesSlice.actions.setIsLoading(true))
		const res = yield* call(bankEmployeesApi.createOne, data)
		callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(bankEmployeesSlice.actions.setIsLoading(false))
	}
}

function* updateOne(action: BankEmployeesNS.UpdateOne) {
	try {
		const { id, data, callback } = action.payload
		yield* put(bankEmployeesSlice.actions.setIsLoading(true))
		const res = yield* call(bankEmployeesApi.updateOne, id, data)
		callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(bankEmployeesSlice.actions.setIsLoading(false))
	}
}

function* getOne(action: BankEmployeesNS.GetOne) {
	try {
		const { id, callback } = action.payload
		const data = yield* select(bankEmployeesSelectors.selectAll)
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(bankEmployeesApi.getOne, id)
		const index = data.entries.findIndex((item) => item.id === id)
		data.entries[index] = Deserialize(res, BankEmployeesModel)
		yield* put(bankEmployeesSlice.actions.setAll(data))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getSubstitutionInfo(action: BankEmployeesNS.GetSubstitutionInfo) {
	try {
		const res = yield* call(bankEmployeesApi.getSubstitutionInfo)
		yield* put(bankEmployeesSlice.actions.setSubstitutionInfo(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getLoginSubstitution(action: BankEmployeesNS.GetLoginSubstitution) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const idUser = action.payload
		const res = yield* call(bankEmployeesApi.getLoginSubstitution, idUser)
		yield* call(LocalStorage.setSubstitution, res)
		yield* put(socketSlice.actions.closeConnection({ isNeedClearChannels: true }))
		yield* put(routerActions.push(FrontUrls.root))
		window.location.reload()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export function* bankEmployeesSaga() {
	yield* takeLatest(bankEmployeesSlice.actions.getAll.type, getAll)
	yield* takeLatest(bankEmployeesSlice.actions.createOne.type, createOne)
	yield* takeLatest(bankEmployeesSlice.actions.updateOne.type, updateOne)
	yield* takeLatest(bankEmployeesSlice.actions.getOne.type, getOne)
	yield* takeLatest(bankEmployeesSlice.actions.getSubstitutionInfo.type, getSubstitutionInfo)
	yield* takeLatest(bankEmployeesSlice.actions.getLoginSubstitution.type, getLoginSubstitution)
}
